import React from "react";
import {
  FaRegComment,
  FaRetweet,
  FaRegHeart,
  FaShare,
} from "react-icons/fa";
import { getProfilePicture } from "../../../utils/avatar";

/**
 * Read-only social media post card for use in survey stimulus perception.
 *
 * Visually matches the platform's Message.js component but has zero
 * interactivity — no click handlers, no routing, no socket/API deps.
 *
 * Props:
 *   post        - { author: { username, displayName }, content, timestamp, likes, comments, reposts }
 *   comment     - optional comment object (same shape minus comments/reposts)
 *   highlightComment - if true, visually emphasize the comment as the rating target
 */
const StimulusPostCard = ({ post, comment, highlightComment = false }) => {
  if (!post) return null;

  const {
    author = {},
    content = "",
    timestamp = "",
    likes = 0,
    comments: commentCount = 0,
    reposts = 0,
  } = post;

  return (
    <div className="rounded-xl border border-spiders-border bg-spiders-bg-surface overflow-hidden">
      {/* Post */}
      <div className="p-4">
        <div className="flex space-x-3">
          {/* Avatar */}
          <div className="flex-shrink-0">
            <img
              src={getProfilePicture(author.username || "unknown")}
              alt={author.username}
              className="w-10 h-10 rounded-full"
            />
          </div>

          {/* Content area */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 mb-1">
              <span className="font-bold text-spiders-text-primary">
                {author.displayName || author.username || "Unknown"}
              </span>
              <span className="text-spiders-text-muted text-sm">
                @{author.username || "unknown"}
              </span>
              <span className="text-spiders-text-muted">·</span>
              <span className="text-spiders-text-muted text-sm">
                {timestamp}
              </span>
            </div>

            {/* Post content */}
            <div className="text-spiders-text-primary text-[15px] leading-normal whitespace-pre-wrap">
              {content}
            </div>

            {/* Static action bar */}
            <div className="flex items-center mt-3 -ml-2">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-spiders-text-muted mr-1">
                <FaRegComment className="w-4 h-4" />
                {commentCount > 0 && (
                  <span className="text-xs">{commentCount}</span>
                )}
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-spiders-text-muted mr-1">
                <FaRetweet className="w-4 h-4" />
                {reposts > 0 && (
                  <span className="text-xs">{reposts}</span>
                )}
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-spiders-text-muted mr-1">
                <FaRegHeart className="w-4 h-4" />
                {likes > 0 && (
                  <span className="text-xs">{likes}</span>
                )}
              </div>
              <div className="flex items-center px-3 py-1.5 rounded-full text-spiders-text-muted">
                <FaShare className="w-4 h-4" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comment (if present) */}
      {comment && (
        <div
          className={`border-t border-spiders-border ${
            highlightComment
              ? "border-l-4 border-l-spiders-primary-400 bg-spiders-primary-500/5"
              : ""
          }`}
        >
          {highlightComment && (
            <div className="px-4 pt-3 pb-0">
              <span className="text-xs font-medium text-spiders-primary-400 uppercase tracking-wider">
                Rate this comment
              </span>
            </div>
          )}
          <div className="flex space-x-3 px-4 py-3 pl-8">
            {/* Comment avatar */}
            <div className="flex-shrink-0">
              <img
                src={getProfilePicture(comment.author?.username || "unknown")}
                alt={comment.author?.username}
                className="w-8 h-8 rounded-full border border-spiders-border"
              />
            </div>

            {/* Comment content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-1 mb-1">
                <span className="font-bold text-spiders-text-primary text-sm">
                  {comment.author?.displayName || comment.author?.username || "Unknown"}
                </span>
                <span className="text-spiders-text-muted text-xs">
                  @{comment.author?.username || "unknown"}
                </span>
                <span className="text-spiders-text-muted text-xs">·</span>
                <span className="text-spiders-text-muted text-xs">
                  {comment.timestamp || ""}
                </span>
              </div>
              <p className="text-spiders-text-primary text-sm leading-relaxed">
                {comment.content}
              </p>
              {/* Static comment action bar */}
              <div className="flex items-center space-x-4 mt-2">
                <div className="flex items-center space-x-1.5 text-xs text-spiders-text-muted">
                  <FaRegHeart className="w-3 h-3" />
                  <span>{comment.likes || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StimulusPostCard;
